import React, { useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Banner, Select } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { ProfileIcon } from '@/components/Icons';
import { ProfileType, ProfileInfo } from '@/types';
import theme from './CopyProjectModal.module.scss';
import { useAppSelector } from '@/utils/hooks';

interface CopyProjectModalProps {
    isOpen: boolean;
    toggle: () => void;
    onSave: (projectName: string, selectedProfileId: number) => Promise<void>;
    orginProjectName: string;
}

const CopyProjectModal: React.FC<CopyProjectModalProps> = ({ isOpen, toggle, onSave, orginProjectName }) => {
    const [projectName, setProjectName] = useState(orginProjectName + ' - Copy');
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const allProfiles = useAppSelector((state) => state.global.profiles || []);
    const activeProfile = useAppSelector((state) => state.global.activeProfile);

    const availableProfiles = useMemo(() => {
        if (!activeProfile) {
            return [];
        }
        return allProfiles.filter((profile: ProfileInfo) => profile.profileType === ProfileType.Seller);
    }, [allProfiles, activeProfile]);

    const selectOptions = useMemo(
        () =>
            availableProfiles.map((p) => ({
                value: p.profileId,
                label: (
                    <div className={theme.profileContent}>
                        <ProfileIcon sizeInPx={20} profileId={p.profileId} className={theme.profileIcon} />
                        <span className={theme.profileName}>{p.profileName}</span>
                    </div>
                ),
            })),
        [availableProfiles]
    );

    const selectedOption = useMemo(
        () => (selectedProfileId ? (selectOptions.find((o) => o.value === selectedProfileId) ?? null) : null),
        [selectOptions, selectedProfileId]
    );

    const handleSelectChange = (newValue: any) => {
        setSelectedProfileId(newValue ? newValue.value : null);
    };

    const save = async () => {
        setLoading(true);
        try {
            await onSave(projectName, selectedProfileId!);
        } finally {
            setLoading(false);
        }
        toggle();
    };

    const reset = () => {
        setProjectName(orginProjectName + ' - Copy');
        if (activeProfile) {
            setSelectedProfileId(activeProfile.id);
            return;
        }
        setSelectedProfileId(availableProfiles.length > 0 ? availableProfiles[0].profileId : null);
    };
    return (
        <Modal toggle={toggle} isOpen={isOpen} onOpen={reset}>
            <ModalHeader>
                <Trans>Copy project</Trans>
            </ModalHeader>
            <ModalBody>
                <ValidationForm onSubmit={save}>
                    <div className="form-group">
                        <label>
                            <Trans>Project name</Trans>
                        </label>
                        <ValidatingInput
                            fieldName={t`Project name`}
                            required
                            className="form-control"
                            placeholder={t`New project name`}
                            value={projectName}
                            name="projectName"
                            onChange={(e) => setProjectName(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <Trans>Copy to profile</Trans>
                        </label>
                        <Select
                            fieldName={t`Copy to profile`}
                            required
                            options={selectOptions}
                            value={selectedOption as any}
                            onChange={handleSelectChange}
                            placeholder={t`Select profile...`}
                            className="full-width"
                            isClearable={availableProfiles.length > 0}
                        />
                    </div>
                    <ValidationSummary />
                    <Banner type="info">
                        <Trans>
                            This will create a new project as a copy of project <b>{orginProjectName}</b>. The project will
                            copy:
                            <ul>
                                <li>Project details</li>
                                <li>Tags</li>
                                <li>Options</li>
                                <li>Styles</li>
                            </ul>
                        </Trans>
                    </Banner>
                    <ButtonBar>
                        <ButtonBar.Left>
                            <Button
                                color="tertiary"
                                onClick={() => {
                                    toggle();
                                }}>
                                <Trans>Cancel</Trans>
                            </Button>
                        </ButtonBar.Left>
                        <ButtonBar.Right>
                            <Button type="submit" color="primary" loading={loading} disabled={!selectedProfileId}>
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </ModalBody>
        </Modal>
    );
};

export default CopyProjectModal;
